import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";

// Default size (PDF points) for a freshly dropped text box.
const DEFAULT_W = 160;
const DEFAULT_H = 20;

let _tempSeq = 0;
const tempFieldId = () => `temp_${Date.now()}_${_tempSeq++}`;
export const isTempField = (id) => typeof id === "string" && id.startsWith("temp_");

/**
 * Acrobat-style "Add Text" annotation for manual mode on flat PDFs.
 *
 *   - createAt(page, x, y): drop a new text field at a click point and open it
 *     for inline editing right away.
 *   - startEdit(field) / commitEdit() / cancelEdit(): inline edit an existing
 *     annotation. Commit PATCHes the answer; a just-created box left empty is
 *     removed so stray clicks don't litter the page.
 *
 * Fields are plain AutoPDFFields (user_overridden), so Finalize bakes them via
 * the annotation overlay — the same path the draw-box tool already uses.
 */
export default function useManualAnnotate({ jobId, setJob, isFinalizedView }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // Tracks a box that was just created so we can delete it if left blank.
  const [pendingNewId, setPendingNewId] = useState(null);

  // Optimistic create: render the field instantly with a temp id, POST in the
  // background, then swap in the real record (or roll back on failure).
  const optimisticCreate = useCallback(
    async (payload) => {
      if (isFinalizedView) return null;
      const tempId = tempFieldId();
      const optimistic = {
        id: tempId,
        field_id: tempId,
        field_type: payload.field_type || "text",
        label: "",
        section: "",
        page: payload.page,
        bbox: payload.bbox,
        write_targets: [payload.bbox],
        options: null,
        answer: payload.answer ?? null,
        confidence: payload.answer != null ? 1.0 : null,
        sources: [],
        user_overridden: true,
        needs_review: false,
        _pending: true,
      };
      setJob((prev) =>
        prev ? { ...prev, fields: [...(prev.fields || []), optimistic] } : prev
      );
      try {
        const res = await api.post(`/api/v1/rfp/autopdf/jobs/${jobId}/fields/`, payload);
        if (![200, 201].includes(res.status)) throw new Error("create failed");
        const real = res.data;
        setJob((prev) =>
          prev
            ? {
                ...prev,
                fields: (prev.fields || []).map((f) => (f.id === tempId ? real : f)),
              }
            : prev
        );
        return { tempId, real };
      } catch (err) {
        console.error("Create field failed:", err);
        setJob((prev) =>
          prev
            ? { ...prev, fields: (prev.fields || []).filter((f) => f.id !== tempId) }
            : prev
        );
        toast.error(err.response?.data?.error || err.message || "Failed to add field.");
        return null;
      }
    },
    [jobId, setJob, isFinalizedView]
  );

  const startEdit = useCallback((field) => {
    setEditingId(field.id);
    setDraft(field.answer || "");
    setPendingNewId(null);
  }, []);

  const cancelEdit = useCallback(async () => {
    // Drop an empty just-created box on cancel.
    if (pendingNewId != null) {
      const id = pendingNewId;
      setJob((prev) =>
        prev ? { ...prev, fields: (prev.fields || []).filter((f) => f.id !== id) } : prev
      );
      try {
        await api.delete(`/api/v1/rfp/autopdf/jobs/${jobId}/fields/${id}/`);
      } catch {
        /* best-effort cleanup */
      }
    }
    setEditingId(null);
    setDraft("");
    setPendingNewId(null);
  }, [pendingNewId, jobId, setJob]);

  const createAt = useCallback(
    async (pageIndex, pdfX, pdfY) => {
      const bbox = [pdfX, pdfY, pdfX + DEFAULT_W, pdfY + DEFAULT_H];
      const result = await optimisticCreate({ page: pageIndex, bbox, answer: null });
      // Open the inline editor on the real field once the server confirms.
      if (result) {
        setEditingId(result.real.id);
        setDraft("");
        setPendingNewId(result.real.id);
      }
    },
    [optimisticCreate]
  );

  // Place a checkbox "stamp" (ticked) at a click point.
  const createCheckAt = useCallback(
    (pageIndex, pdfX, pdfY) => {
      const S = 16;
      const bbox = [pdfX - S / 2, pdfY - S / 2, pdfX + S / 2, pdfY + S / 2];
      return optimisticCreate({
        page: pageIndex,
        bbox,
        answer: "true",
        field_type: "checkbox",
      });
    },
    [optimisticCreate]
  );

  // Place a signature image (PNG data URL) at a click point.
  const createSignatureAt = useCallback(
    (pageIndex, pdfX, pdfY, dataUrl, aspect = 0.35) => {
      if (!dataUrl) return undefined;
      const W = 160;
      const H = Math.max(28, Math.round(W * aspect));
      const bbox = [pdfX, pdfY, pdfX + W, pdfY + H];
      return optimisticCreate({
        page: pageIndex,
        bbox,
        answer: dataUrl,
        field_type: "signature",
      });
    },
    [optimisticCreate]
  );

  // Toggle a checkbox field on/off.
  const toggleCheck = useCallback(
    async (field) => {
      const truthy = ["true", "1", "yes", "checked", "on"].includes(
        String(field.answer).trim().toLowerCase()
      );
      const next = truthy ? "false" : "true";
      setJob((prev) =>
        prev
          ? {
              ...prev,
              fields: (prev.fields || []).map((f) =>
                f.id === field.id
                  ? { ...f, answer: next, user_overridden: true, needs_review: false }
                  : f
              ),
            }
          : prev
      );
      try {
        await api.patch(`/api/v1/rfp/autopdf/jobs/${jobId}/fields/${field.id}/`, {
          answer: next,
        });
      } catch (err) {
        console.error("Toggle check failed:", err);
        toast.error("Failed to toggle check.");
      }
    },
    [jobId, setJob]
  );

  const commitEdit = useCallback(async () => {
    if (editingId == null) return;
    const id = editingId;
    const value = draft;

    // Empty + brand new → remove the stray box instead of saving blank.
    if (!value.trim() && pendingNewId === id) {
      setEditingId(null);
      setDraft("");
      setPendingNewId(null);
      setJob((prev) =>
        prev ? { ...prev, fields: (prev.fields || []).filter((f) => f.id !== id) } : prev
      );
      try {
        await api.delete(`/api/v1/rfp/autopdf/jobs/${jobId}/fields/${id}/`);
      } catch {
        /* best-effort */
      }
      return;
    }

    setBusy(true);
    try {
      const res = await api.patch(
        `/api/v1/rfp/autopdf/jobs/${jobId}/fields/${id}/`,
        { answer: value }
      );
      const updated = res.data || {};
      setJob((prev) => {
        if (!prev) return prev;
        const fields = (prev.fields || []).map((f) =>
          f.id === id
            ? {
                ...f,
                answer: updated.answer ?? value,
                user_overridden: true,
                needs_review: false,
                confidence: 1.0,
              }
            : f
        );
        return { ...prev, fields };
      });
    } catch (err) {
      console.error("Save text failed:", err);
      toast.error(err.response?.data?.error || err.message || "Failed to save text.");
    } finally {
      setBusy(false);
      setEditingId(null);
      setDraft("");
      setPendingNewId(null);
    }
  }, [editingId, draft, pendingNewId, jobId, setJob]);

  return {
    editingId,
    draft,
    setDraft,
    busy,
    startEdit,
    cancelEdit,
    createAt,
    createCheckAt,
    createSignatureAt,
    toggleCheck,
    commitEdit,
  };
}
